/**
 * Route definition for /hall-of-fame
 * Renders the Profile component which displays the Hall of Fame leaderboard.
 *
 * @module routes/hall-of-fame
 * @category Routes
 */
import { createFileRoute } from '@tanstack/react-router'
import Breadcrumb from "@/components/Breadcrumb/index.jsx";
import React, { useEffect, useState } from "react";
import { getHallOfFame } from "@/lib/api";
import { handelTitle } from "@/utils/index.js";
import { useNavigate } from "@tanstack/react-router";


export const Route = createFileRoute('/$locale/hall-of-fame')({
  component: Profile,
})

/**
 * Hall of Fame page component.
 * Displays a leaderboard of top users across various statistics like logins,
 * comments, listings, auction wins, and more. Users can navigate to profiles.
 *
 * @returns {React.ReactElement}
 */
function Profile() {
    const [hofloc, setHofloc] = useState("Most Logins");
    const [hofdat, setHofdat] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentStat, setCurrentStat] = useState("logins");

    const navigate = useNavigate();

    const imgErr = (e) => {
        e.target.onError = null;
        e.target.src = e.target.id.includes('avatar_') ? "https://cdn.stealthisnft.xyz/u/0/profile/avatar.jpg" : null;
    };

    const getStat = async (type, e) => {
        const label = e?.target?.innerText || type;
        setHofloc(label);
        setCurrentStat(type);
        setCurrentPage(1); // reset to page 1 on new stat
    };

    // Fetch data whenever currentStat or currentPage changes
    useEffect(() => {
        const fetchHofData = async () => {
            try {
                const result = await getHallOfFame({
                    stat: currentStat,
                    page: currentPage,
                    limit: 20,
                });
                setHofdat(result.data);
                setPagination(result.pagination);
            } catch (error) {
                console.error("Failed to fetch hall of fame:", error);
                setHofdat([]);
            }
        };
        fetchHofData();
    }, [currentStat, currentPage]);

    useEffect(() => {
        handelTitle("Hall of Fame");
    }, []);

    useEffect(() => {
    }, [hofdat])


    return (
        <>
            <main className="main-content mt-1 border-radius-lg">
                <Breadcrumb text1={"Hall of Fame"} text2={hofloc}/>
                <div style={{display: "flex", flexDirection: "row", flexWrap: "wrap"}}>
                    <div style={{flex: 1, maxWidth: "30%"}}
                         className="card-purple vh-100 text-tiny text-bolder p-2 me-2">
                        <h6 className="text-light text-bold">Categories</h6>
                        <br/>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("online", e)}>Recently Online</div>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("logins", e)}>Most Logins</div>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("comments", e)}>Most Comments</div>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("listings", e)}>Most Listings</div>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("won_Auction", e)}>Most Auctions Won
                        </div>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("outbid", e)}>Most Outbid</div>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("totalBids", e)}>Most Bids</div>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("totalSales", e)}>Most Sales</div>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("won_BuyNow", e)}>Most Buy Nows</div>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("profileUpdate", e)}>Most Profile
                            Updates
                        </div>
                        <div className="raisedBoxPurple" onClick={(e) => getStat("logins_failed", e)}>Most Failed
                            Logins
                        </div>


                    </div>
                    <div style={{fontSize: "1.0em", flex: 1, width: "70%"}}
                         className="text-bolder card-purple me-2 p-2">
                        {hofdat ? hofdat.map((item, idx) =>
                            <div key={item.id || idx} onClick={() => {
                                navigate({ to: "/profile/" + item?.id });
                            }} style={{display: "flex", verticalAlign: "top"}} className="raisedBoxPurple">
                                <img
                                    onError={(e) => imgErr(e)}
                                    id={"avatar_" + item?.id}
                                    style={{borderRadius: "0.2em", marginRight: "1em", maxHeight: "2.8em"}}
                                    src={"https://cdn.stealthisnft.xyz/u/" + item.id + "/profile/avatar.jpg"}
                                    alt="avatar"
                                />
                                <div style={{flex: 1}}>
                                    # {idx + 1 + (pagination ? (currentPage - 1) * (pagination.limit || 20) : 0)} (
                                    {hofloc === "Recently Online"
                                        ? new Date(item.lastact * 1000).toLocaleTimeString() + " " + new Date(item.lastact * 1000).toLocaleDateString()
                                        : item.count
                                    }
                                    )<br/>{item.username}
                                </div>
                            </div>
                        ) : null}

                        {/* Pagination controls */}
                        {pagination && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={!pagination.hasPrev}
                                    className="btn btn-sm"
                                    style={{ opacity: pagination.hasPrev ? 1 : 0.5 }}
                                >
                                    ← Previous
                                </button>
                                <span style={{ alignSelf: 'center' }}>
                                    Page {pagination.page} of {pagination.totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => p + 1)}
                                    disabled={!pagination.hasNext}
                                    className="btn btn-sm"
                                    style={{ opacity: pagination.hasNext ? 1 : 0.5 }}
                                >
                                    Next →
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </>
    );
}